import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import GenericModals from '@/components/GenericModals/GenericModals';
import Page from '@/components/Page/Page';
import { extractContents } from '@/helpers/extractContents';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import { PageModel } from '@/models/page';
import Container from '@uikit/components/Container/Container';
import {
  ContainerChildrenSpace,
  ContainerSize,
} from '@uikit/components/Container/consts';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import Layout from '@uikit/components/Layout/Layout';
import PressHeader from '@uikit/components/Press/PressHeader/PressHeader';
import PromoBanner from '@uikit/components/PromoBanner/PromoBanner';
import PressArticleSection from '@uikit/composites/PressArticleSection/PressArticleSection';
import getBaseUrl from '@uikit/helpers/getBaseUrl';
import { CmsComponentsContext } from '../CmsComponentsContext';
import { cmsComponents } from './CommunalArticlePage.cmsComponents';
import type { CommunalArticlePageProps } from './types';

const baseUrl = getBaseUrl();

const CommunalArticlePage = (
  routeData: PageModel<CommunalArticlePageProps>
) => {
  const {
    contents,
    modals,
    path,
    props: {
      title,
      lastModified,
      teaserText,
      image,
      imageCaption,
      publicationDate,
      promoBannerVisible,
      promoBanner,
    },
  } = routeData;
  const meta = extractPageMetaFromRouteData(routeData, {
    ogType: 'article',
  });

  const {
    extractedContents: [headerArea, additionalContentsArea],
    restContents,
  } = extractContents(['header', 'additional-contents'], contents);

  const [{ contents: additionalContents }] = additionalContentsArea ?? [{}];

  const parentPagePath = path.substring(0, path.lastIndexOf('/')) || '/';
  const url = new URL(path, baseUrl).toString();
  const headerDate = publicationDate || lastModified;

  useInitialPageDataLayer([
    {
      event: 'pageView',
    },
  ]);

  return (
    <CmsComponentsContext.Provider value={cmsComponents}>
      <Layout>
        <Page meta={meta}>
          {headerArea && <ContentsToReact contents={headerArea} />}
          <PressArticleSection>
            <Container
              size={ContainerSize.TinyPlus}
              spaceBetweenChildren={ContainerChildrenSpace.Small}
            >
              <PressHeader
                date={headerDate}
                headline={title}
                asset={image}
                imageCaption={imageCaption}
                shareData={{
                  text: teaserText,
                  title: title,
                  url,
                }}
                backButtonPath={parentPagePath}
              />
              <ContentsToReact contents={restContents} />
            </Container>
          </PressArticleSection>

          {additionalContents && (
            <ContentsToReact contents={additionalContents} />
          )}
        </Page>

        <EnercityFooter />
      </Layout>

      {promoBannerVisible === 'promoBanner' && promoBanner && (
        <PromoBanner banner={promoBanner} />
      )}

      <GenericModals modals={modals} />
    </CmsComponentsContext.Provider>
  );
};

export default CommunalArticlePage;
